import React, { useEffect, useState } from "react";
import HalfHero from "../Images/main.jpg";
import Navbar from "../Components/Navbar";
import SearchBar from "../Components/SearchBar";
import { catalogApi } from "../api/client";
import { useNavigate } from "react-router-dom";

// Using Lorem Picsum with category-specific seeds for consistent, relevant images
const PLACEHOLDER_IMAGE = "https://via.placeholder.com/400x300?text=No+Image";

// Image cache to avoid repeated requests
const imageCache = new Map();

const categoryImageSeeds = {
  "Software & IT": 1,
  Healthcare: 2,
  Education: 3,
  "Food & Beverage": 4,
  Automotive: 5,
  "Beauty & Wellness": 6,
  "Home & Garden": 7,
  "Sports & Fitness": 8,
  "Travel & Tourism": 9,
  "Real Estate": 10,
  "Legal Services": 11,
  "Financial Services": 12,
  "Emergency Services": 13,
  "Cleaning Services": 14,
  "Repair Services": 15,
  Photography: 16,
  "Event Planning": 17,
  Consulting: 18,
  Transportation: 19,
  Entertainment: 20,
};

function getCategoryImage(categoryName) {
  if (imageCache.has(categoryName)) {
    return imageCache.get(categoryName);
  }
  let seed = categoryImageSeeds[categoryName];
  if (!seed) {
    seed = categoryName.split("").reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
    seed = Math.abs(seed) % 1000;
  }
  const imageUrl = `https://picsum.photos/seed/${seed}/400/300`;
  imageCache.set(categoryName, imageUrl);
  return imageUrl;
}

async function fetchCategoryImage(categoryName) {
  return getCategoryImage(categoryName);
}

const Category = () => {
  const [categories, setCategories] = useState([]);
  const [categoryImages, setCategoryImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const [visibleCount, setVisibleCount] = useState(12);
  const navigate = useNavigate();

  useEffect(() => {
    catalogApi
      .listCategories()
      .then((data) => setCategories(data.items || []))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, []);

  // Fetch images for all categories when categories change
  // Fetch images for all categories when categories change
  useEffect(() => {
    async function loadImages() {
      if (categories.length === 0) return;

      setImagesLoading(true);
      setImagesLoaded(false);

      const images = {};
      const loadPromises = [];

      // Load images in parallel for better performance
      for (const cat of categories) {
        const promise = fetchCategoryImage(cat.name)
          .then((imageUrl) => {
            images[cat._id] = imageUrl;
          })
          .catch((error) => {
            console.error(`Failed to load image for "${cat.name}":`, error);
            images[cat._id] = PLACEHOLDER_IMAGE;
          });
        loadPromises.push(promise);
      }

      try {
        await Promise.all(loadPromises);
        setCategoryImages(images);
        setImagesLoaded(true);
      } catch (error) {
        console.error(`💥 Error loading images:`, error);
        setCategoryImages(images); // Set whatever we managed to load
        setImagesLoaded(true);
      } finally {
        setImagesLoading(false);
      }
    }

    loadImages();
  }, [categories]);

  const handleCategoryClick = (category) => {
    // Navigate to service page, passing categoryId and a flag to load all subcategory services
    navigate(`/services?categoryIds=${category._id}`);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      });
    });
  };

  const handleShowMore = () => {
    setVisibleCount((prev) => Math.min(prev + 6, categories.length));
  };

  return (
    <>
      <section className="relative w-full h-1/2 overflow-hidden">
        {/* Background Image (with blur) */}
        <img
          src={HalfHero}
          alt="Hero Background"
          className="absolute inset-0 w-full h-full object-cover z-[-10] blur-sm"
        />

        {/* Navbar */}
        <Navbar />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/70 z-[-5]" />

        {/* Content on top of image */}
        <div className="relative z-10 h-full text-white flex flex-col justify-center items-center">
          <div className="h-3/4 w-3/4 text-white flex flex-col justify-center items-center gap-5">
            <h1 className="text-xl font-bold text-center mulish-light uppercase">
              Welcome to <br />
              <span className="text-xl font-bold md:text-5xl md:mulish-bold tracking-wider bg-gradient-to-r from-orange-500 via-white to-green-500 bg-clip-text text-transparent ">
                ONLINE INDIAN MARKET
              </span>
            </h1>
            <p className="mt-0 md:mt-2 text-sm mulish-light tracking-wider text-center uppercase">
              Get the Nearest and Best Services in the Market.
            </p>
            <SearchBar />
            <div className="flex flex-row justify-around w-3/4"></div>
          </div>
        </div>
      </section>
      <section className="flex flex-col justify-center items-center py-12 px-4 w-full bg-gray-100">
        <h2 className="poppins-bold text-3xl mb-8 text-gray-800 text-center">
          All Categories
        </h2>
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        ) : (
          <div className="flex flex-col items-center w-full">
            <div className="grid grid-cols-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-7xl mx-auto px-4 w-full">
              {categories.length === 0 ? (
                <div className="col-span-full text-center text-gray-700 bg-white/60 rounded-md p-10 border border-dashed">
                  No categories found.
                </div>
              ) : (
                categories.slice(0, visibleCount).map((item, idx) => (
                  <div
                    key={item._id || idx}
                    className="relative w-full  aspect-square rounded-2xl overflow-hidden border border-white shadow-md group cursor-pointer"
                    onClick={() => handleCategoryClick(item)}
                  >
                    <img
                      src={categoryImages[item._id] || PLACEHOLDER_IMAGE}
                      alt={item.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => {
                        console.warn(
                          `🖼️ Image failed to load for "${item.name}", using placeholder`
                        );
                        e.target.src = PLACEHOLDER_IMAGE;
                      }}
                      onLoad={() => {}}
                    />
                    <div className="absolute inset-0 bg-black/50 transition group-hover:bg-gradient-to-br from-orange-500 to-green-900 group-hover:opacity-80" />
                    <div className="relative z-10 h-full w-full flex flex-col justify-center items-center text-white text-center p-3">
                      <span className="text-xs sm:text-base md:text-xl font-semibold text-gray-900 px-3 py-1 bg-white/40 poppins-regular rounded-2xl">
                        {item.name}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
            {visibleCount < categories.length && (
              <button
                onClick={handleShowMore}
                className="mt-8 px-6 py-2 bg-blue-500 text-white rounded-lg shadow hover:bg-blue-600 transition"
              >
                Show More
              </button>
            )}
          </div>
        )}
      </section>
    </>
  );
};

export default Category;
